/**
 * Граница ошибок.
 *
 * Упавший компонент не должен уносить всё приложение: пользователь теряет
 * несохранённый ввод в соседних панелях, а не только сломанный виджет.
 */

import * as React from "react";
import { RotateCcw, TriangleAlert } from "lucide-react";

import { Button } from "@/shared/ui/Button";
import { EmptyState } from "@/shared/ui/Feedback";

interface Props {
  children: React.ReactNode;
  /** Название области: в сообщении видно, что именно сломалось. */
  area?: string;
}

interface State {
  error: Error | null;
}

export class ErrorBoundary extends React.Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: React.ErrorInfo): void {
    // Отправка в наблюдаемость появится вместе с фронтовой телеметрией;
    // до тех пор консоль — единственное место, где это видно разработчику
    console.error("Ошибка в области", this.props.area ?? "приложение", error, info);
  }

  override render(): React.ReactNode {
    if (this.state.error === null) {
      return this.props.children;
    }

    return (
      <EmptyState
        icon={<TriangleAlert className="size-[32px] text-[var(--pt-danger-text)]" />}
        title="Этот блок не удалось показать"
        description={
          this.props.area
            ? `Ошибка в области «${this.props.area}». Остальная часть страницы работает.`
            : "Ошибка интерфейса. Остальная часть страницы работает."
        }
        action={
          <Button
            icon={<RotateCcw className="size-[var(--pt-icon)]" />}
            onClick={() => this.setState({ error: null })}
          >
            Попробовать снова
          </Button>
        }
      />
    );
  }
}
